import { computed, DestroyRef, effect, inject, Injectable, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { HttpClient } from '@angular/common/http';

import { dialog, SntDateService, SntFormatPriceService } from '@senet-uiverse/common';
import { BehaviorSubject, catchError, EMPTY, map, of, switchMap, tap } from 'rxjs';
import { filterSuccessResult, injectQuery } from '@ngneat/query';
import { LanguageCode } from '@senet-uiverse/i18n';

import { ECounterType } from '../enums';
import {
  IZone,
  ITopUpData,
  IOrganization,
  IRequiredField,
  IOrganizationInfo,
  IRequiredFieldFull,
  IKnowAboutUsAnswer,
  IOrganizationValidate,
  IRegisterLocalAccountData,
  IStorageOrganizationProfile,
  ISendTemporaryPasswordResponse,
  IStorageOrganizationProfileUser,
} from '../types';
import { IOffice } from '../types/offices';

import { StorageService } from './storage.service';

@Injectable({ providedIn: 'root' })
export class OrganizationService {
  private readonly formatPriceService = inject(SntFormatPriceService);
  private readonly storageService = inject(StorageService);
  private readonly dateService = inject(SntDateService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly http = inject(HttpClient);
  private readonly query = injectQuery();
  private readonly dialog = dialog();

  readonly organizationList = signal<IOrganization[]>([]);
  readonly selectedOrganizationTitle = computed(() => {
    return this.organizationList().find((org) => org.id === this.storageService.storage().organization)?.name || null;
  });
  readonly currentOrganization$ = new BehaviorSubject<string | null>(null);
  readonly isLoadingProfileData = signal(false);
  readonly orgLang = computed(() => {
    const organizationInfo = this.storageService.storage().organizationInfo;

    return organizationInfo?.lang || LanguageCode.en;
  });

  private organization: string | null = null;

  constructor() {
    effect(() => {
      const organization = this.storageService.storage().organization;

      if (!organization) {
        this.organization = null;
        this.currentOrganization$.next(null);
      }

      if (organization && this.organization !== organization) {
        this.currentOrganization$.next(organization);
        this.organization = organization;
        this.getProfileData();
        this.getOrganizationInfo();
      }
    });
  }

  getOrganizationList() {
    return this.http
      .get<IOrganization[]>('organization/list')
      .pipe(tap((response) => this.organizationList.set(response)));
  }

  validateOrganization(code: Readonly<string>) {
    return this.http.post<IOrganizationValidate>(`organization/validate/${code}`, {});
  }

  login(data: { login: string; password: string; token: string }) {
    return this.http.post<void>('organization/login', data);
  }

  loginByToken(data: { password: string; token: string }) {
    return this.http.post<void>('organization/login_with_keycloak_token', data);
  }

  getOfficeList() {
    const organization = this.storageService.storage().organization;

    return this.query({
      queryKey: [`offices/${organization}`] as const,
      queryFn: () => {
        return this.http.get<IOffice[]>(`organization/${organization}/offices`);
      },
    }).result$.pipe(
      filterSuccessResult(),
      map(({ data }) => data),
    );
  }

  getZoneList() {
    const organization = this.storageService.storage().organization;

    return this.query({
      queryKey: [`zones/${organization}`] as const,
      queryFn: () => {
        return this.http.get<IZone[]>(`organization/${organization}/zones`).pipe(catchError(() => of([] as IZone[])));
      },
    }).result$.pipe(
      filterSuccessResult(),
      map(({ data }) => data),
    );
  }

  updateProfile(data: Readonly<Partial<IStorageOrganizationProfileUser>>) {
    return this.http
      .patch<IStorageOrganizationProfile>(`user/${this.storageService.storage().organization}/me`, data)
      .pipe(
        tap((response) => {
          if (response) {
            this.storageService.update({ organizationProfile: response });
          }
        }),
      );
  }

  getRequiredFields(organizationId = '', short = true) {
    const organization = organizationId || this.storageService.storage().organization;

    const url = `organization/${organization}/registration-fields?short=${short}`;

    return this.query({
      queryKey: [url],
      queryFn: () => this.http.get<IRequiredField[] | IRequiredFieldFull[]>(url),
    }).result$.pipe(
      filterSuccessResult(),
      map(({ data }) => {
        if (short) {
          return data as IRequiredField[];
        }

        return data as IRequiredFieldFull[];
      }),
    );
  }

  removeOrganization(organizationId: Readonly<string>) {
    return this.http.post<void>(`organization/${organizationId}/unbind`, {}).pipe(
      tap(() => {
        this.organizationList.update((list) => list.filter((org) => org.id !== organizationId));

        const currentOrganization = this.storageService.storage().organization;

        if (currentOrganization === organizationId) {
          this.storageService.update({
            organizationProfile: null,
            organizationInfo: null,
            organization: null,
          });
        }
      }),
    );
  }

  checkPaymentAvailability() {
    const organization = this.storageService.storage().organization;

    return this.http.get<{ is_available: boolean }>(`${organization}/check_payment_availability`).pipe(
      switchMap((response) => {
        const isAvailable = response.is_available;

        if (isAvailable) {
          return of(true);
        }

        return this.incrementCount(ECounterType.TopUpBalance).pipe(
          map(() => false),
          catchError(() => of(false)),
        );
      }),
    );
  }

  topUp(amount: Readonly<string>) {
    const organization = this.storageService.storage().organization;

    return this.http.post<ITopUpData>(`${organization}/refill_account`, { amount });
  }

  incrementCount(type: Readonly<ECounterType>) {
    const organization = this.storageService.storage().organization;

    return this.http.post<{ count: number }>(`counter/${organization}/increment`, { counter_type: type });
  }

  // ? Тут ми провіряємо чи існує такий телефон в організації
  checkEmail(data: { email: string; token: string }) {
    return this.http
      .post<{ exists: boolean }>('organization/check_email_exists', data)
      .pipe(map((response) => response.exists));
  }

  // ? Тут ми провіряємо чи існує такий телефон в організації
  checkPhone(data: { phone: string; token: string }) {
    return this.http
      .post<{ exists: boolean }>('organization/check_phone_exists', data)
      .pipe(map((response) => response.exists));
  }

  registerationLocalAccount(data: IRegisterLocalAccountData) {
    return this.http.post<void>('organization/register_user/', data);
  }

  getKnowAboutList(organizationId = '') {
    const organization = organizationId || this.storageService.storage().organization;

    return this.query({
      queryKey: [`know-about-us/${organization}`] as const,
      queryFn: () => this.http.get<IKnowAboutUsAnswer[]>(`organization/${organization}/know-about-us`),
    }).result$.pipe(
      filterSuccessResult(),
      map(({ data }) => data.map((item) => ({ value: item.id, label: item.name }))),
    );
  }

  sendTemporaryPassword(organizationId: Readonly<string>, phoneAsUsername: Readonly<boolean>) {
    return this.http.post<ISendTemporaryPasswordResponse>(
      `organization/${organizationId}/send_temporary_password`,
      {},
      { params: { phone_as_username: String(phoneAsUsername) } },
    );
  }

  resetPassword(data: { password: string; temporary_password: string; phone_as_username: boolean }) {
    return this.http.post<void>('organization/set_new_password/', data);
  }

  private getOrganizationInfo() {
    return this.query({
      queryKey: [`organization/${this.organization}`] as const,
      queryFn: () => this.http.get<IOrganizationInfo>(`organization/${this.organization}`),
    })
      .result$.pipe(
        filterSuccessResult(),
        map(({ data }) => data),
        tap((response) => {
          if (response) {
            this.storageService.update({ organizationInfo: response });

            // ? Set format price options
            this.formatPriceService.options.set({
              decimalDigits: response.display_decimal_places,
              currency: response.currency,
            });

            // ? Set date format
            this.dateService._is24Format.set(response.is_24h_format_enabled);
          }
        }),
        takeUntilDestroyed(this.destroyRef),
        catchError(() => {
          this.storageService.update({ organizationInfo: null });

          return EMPTY;
        }),
      )
      .subscribe();
  }

  private getProfileData() {
    this.isLoadingProfileData.set(true);

    return this.query({
      queryKey: [`user/${this.organization}/me`] as const,
      queryFn: () => {
        return this.http.get<IStorageOrganizationProfile>(`user/${this.organization}/me`);
      },
    })
      .result$.pipe(
        filterSuccessResult(),
        map(({ data }) => data),
        tap((response) => {
          this.isLoadingProfileData.set(false);

          if (response) {
            this.storageService.update({ organizationProfile: response });
          }
        }),
        takeUntilDestroyed(this.destroyRef),
        catchError(() => {
          this.isLoadingProfileData.set(false);

          this.storageService.update({ organizationProfile: null });

          return EMPTY;
        }),
      )
      .subscribe();
  }
}
